import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { useHMG } from "../../../HMGContext";

const DeleteModal = ({
  isOpen,
  setOpenDeleteModal,
  isSingle,
  onClose,
  onSubmit,
  rowData,
  // Custom mode – when provided, overrides default labels/content
  title,
  message,
  itemLabel,
  itemValue,
  // Custom mode only – extra block content (e.g. a list) rendered below the message
  details,
  cancelLabel,
  confirmLabel,
  preventBackdropClose = false,
}) => {
  const { setCurrentModalName } = useHMG();
  const [submitLoading, setSubmitLoading] = useState(false);

  const handleClose = () => {
    if (submitLoading) return;
    if (setOpenDeleteModal) setOpenDeleteModal(false);
    if (onClose) onClose();
    setCurrentModalName?.(null);
  };

  const handleDialogClose = (event, reason) => {
    if (submitLoading) return;
    if (preventBackdropClose && reason === "backdropClick") return;
    handleClose();
  };

  const handleSubmit = async () => {
    if (!onSubmit) return;
    setSubmitLoading(true);
    try {
      const result = onSubmit();
      if (result && typeof result.then === "function") {
        await result;
      }
      setCurrentModalName?.(null);
      handleClose();
    } catch (error) {
      // Keep modal open on error so user can retry or cancel
    } finally {
      setSubmitLoading(false);
    }
  };

  const isCustomMode = Boolean(title || message);
  const isArchiveAction = !isCustomMode && (rowData?.action === "archive" || rowData?.action === "restore");
  const isDuplicateAction = !isCustomMode && rowData?.action === "duplicate";
  const actionText = rowData?.action === "restore" ? "restore" : rowData?.action === "archive" ? "archive" : rowData?.action === "duplicate" ? "duplicate" : "delete";

  const dialogTitle = isCustomMode
    ? title
    : isSingle
    ? (isArchiveAction || isDuplicateAction)
      ? `Are you sure you want to ${actionText} ${rowData?.versionName}?`
      : `Are you sure you want to delete ${rowData?.versionName}?`
    : "Are you sure you want to delete 5 plan version(s)?";

  const dialogMessage = isCustomMode ? (
    <Typography variant="body1" sx={{ fontSize: "14px", lineHeight: "24px", fontWeight: 400 }}>
      {message}
      {itemLabel != null && itemValue != null && (
        <>
          <br />
          <strong>{itemLabel}</strong> {itemValue}
        </>
      )}
    </Typography>
  ) : isSingle ? (
    <Typography id="delete-dialog-description" sx={{ fontSize: "14px", lineHeight: "24px", fontWeight: 400 }}>
      {isDuplicateAction ? "A new template will be created with the same configuration." : "This action cannot be undone."}
    </Typography>
  ) : null;

  const cancelButtonLabel = isCustomMode ? (cancelLabel ?? "Cancel") : (isArchiveAction || isDuplicateAction ? "Cancel" : "No, don't delete");
  const confirmButtonLabel = isCustomMode ? (confirmLabel ?? "Delete") : (isArchiveAction || isDuplicateAction ? `Yes, ${actionText}` : "Yes, delete");

  return (
    <div>
      <Dialog
        open={isOpen}
        onClose={handleDialogClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2 } }}
        disableRestoreFocus
      >
        
        <DialogTitle
          sx={{
            padding: "16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontSize: "20px", fontWeight: 700, color: "#212B36" }}>
              {dialogTitle}
            </Typography>
          </Box>
          <IconButton onClick={(e) => { e.stopPropagation(); handleClose(); }} disabled={submitLoading}>
            <ClearIcon sx={{ width: "16px", height: "16px" }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: "16px" }}>
          {isCustomMode ? (
            <>
              {dialogMessage}
              {details}
            </>
          ) : isSingle ? (
            dialogMessage
          ) : (
            <Box>
              <Typography
                sx={{ color: "#637381", fontSize: "14px", fontWeight: 400 }}>
                The following plans will be deleted:
              </Typography>

              <Typography
                sx={{
                  color: "#212B36",
                  fontSize: "14px",
                  mt: 2,
                  fontWeight: 600,
                }}>
                Skyline luxury hotel
              </Typography>
              <Box
                sx={{
                  paddingLeft: 2,
                  fontSize: "12px",
                  lineHeight: "18px",
                  fontWeight: 400,
                }}>
                <ul style={{ listStyleType: "disc", marginLeft: "20px" }}>
                  <li>2024 | 2024 budget plan | Budget 2.0</li>
                  <li>2024 | 2024 budget plan | Budget 3.0</li>
                  <li>2024 | 2024 budget plan | Budget 4.0</li>
                </ul>
              </Box>

              <Typography
                sx={{
                  color: "#212B36",
                  fontSize: "14px",
                  mt: 2,
                  fontWeight: 600,
                }}>
                Urban Oasis Suites
              </Typography>
              <Box
                sx={{
                  paddingLeft: 2,
                  fontSize: "12px",
                  lineHeight: "18px",
                  fontWeight: 400,
                }}>
                <ul style={{ listStyleType: "disc", marginLeft: "20px" }}>
                  <li>2024 | 2024 budget plan | Budget 2.0</li>
                  <li>2023 | 2023 budget plan | Budget 4.0</li>
                </ul>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "0 16px 16px 16px"
          }}>
          <Button
            type="button"
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleClose(); }}
            variant="outlined"
            disabled={submitLoading}
          >
            {cancelButtonLabel}
          </Button>
          <Button
            type="button"
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleSubmit(); }}
            variant="contained"
            color="error"
            disabled={submitLoading}
            sx={{
              bgcolor: "#FF5630",
              "&:hover": { backgroundColor: "#FF5630", boxShadow: "none" },
            }}
          >
            {submitLoading ? (
              <>
                <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                {confirmButtonLabel}
              </>
            ) : (
              confirmButtonLabel
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DeleteModal;
